import {HANDOFF_MESSAGES} from "../config/messages.ts";
import {
    AppClientTokens,
    HandoffClient,
    HandoffCredentials,
    PlatformSession
} from "../types.ts";
import {
    refreshAccessToken,
    signInWithPlatformSession
} from "./CognitoIdpService.ts";
import {
    discardPlatformSession,
    readPlatformSession,
    readPlatformSessions,
    savePlatformSession,
    saveRememberedAccount
} from "./PlatformSessionStore.ts";
import {signInWithPassword} from "./SrpSignInService.ts";
import {
    HANDOFF_ERROR_CODES,
    HandoffError
} from "./HandoffError.ts";

// Only these mean the stored session itself is bad; anything else (network, throttling, 5xx) keeps it.
const SESSION_REJECTION_CODES = new Set([
    'NotAuthorizedException',
    'UserNotFoundException',
    'InvalidParameterException',
    HANDOFF_ERROR_CODES.unexpectedChallenge,
    HANDOFF_ERROR_CODES.clientMismatch,
]);

const isSessionRejection = (e: unknown) => e instanceof HandoffError && SESSION_REJECTION_CODES.has(e.code);

export const discardSessionsOutside = (clients: HandoffClient[]) => {
    const poolIds = new Set(clients.map((c) => c.poolId));
    for (const session of readPlatformSessions()) {
        if (!poolIds.has(session.poolId)) {
            discardPlatformSession(session.poolId);
        }
    }
};

const resumePlatformSession = async (client: HandoffClient, session: PlatformSession): Promise<AppClientTokens> => {
    if (session.clientId !== client.dedicatedClientId) {
        throw new HandoffError(HANDOFF_ERROR_CODES.clientMismatch);
    }

    const refreshed = await refreshAccessToken(session.clientId, session.refreshToken);
    if (refreshed.refreshToken !== session.refreshToken) {
        savePlatformSession({...session, refreshToken: refreshed.refreshToken, savedAt: new Date().toISOString()});
    }

    return signInWithPlatformSession(client.appClientId, session.username, refreshed.accessToken);
};

// Tries every stored session for this request's pools; a rejected one is discarded silently.
export const resumeAnyPlatformSession = async (clients: HandoffClient[]): Promise<AppClientTokens | null> => {
    discardSessionsOutside(clients);

    for (const client of clients) {
        const session = readPlatformSession(client.poolId);
        if (session === null) {
            continue;
        }
        try {
            return await resumePlatformSession(client, session);
        } catch (e) {
            if (isSessionRejection(e)) {
                discardPlatformSession(client.poolId);
            }
        }
    }

    return null;
};

export const signInErrorMessage = (e: unknown) => {
    if (e instanceof HandoffError) {
        switch (e.code) {
            case HANDOFF_ERROR_CODES.accountNotFound:
            case HANDOFF_ERROR_CODES.poolNotAllowed:
                return HANDOFF_MESSAGES.accountNotFound;
            case 'NotAuthorizedException':
            case 'UserNotFoundException':
                return HANDOFF_MESSAGES.incorrectCredentials;
            case HANDOFF_ERROR_CODES.unsupportedChallenge:
            case 'UserNotConfirmedException':
            case 'PasswordResetRequiredException':
                return HANDOFF_MESSAGES.unsupportedAccount;
            case HANDOFF_ERROR_CODES.completeFailed:
                return HANDOFF_MESSAGES.completeFailed;
        }
    }
    return HANDOFF_MESSAGES.signInFailed;
};

// `client` is the request's entry for the user's pool, resolved at the email step.
export const signInWithCredentials = async (client: HandoffClient, credentials: HandoffCredentials): Promise<AppClientTokens> => {
    const platform = await signInWithPassword({
                                                  poolId: client.poolId,
                                                  clientId: client.dedicatedClientId,
                                                  email: credentials.email,
                                                  password: credentials.password,
                                              });

    savePlatformSession({
                            poolId: client.poolId,
                            clientId: client.dedicatedClientId,
                            username: platform.username,
                            refreshToken: platform.refreshToken,
                            savedAt: new Date().toISOString(),
                        });
    saveRememberedAccount({email: credentials.email, poolId: client.poolId});

    try {
        return await signInWithPlatformSession(client.appClientId, platform.username, platform.accessToken);
    } catch {
        // Distinguishes a CUSTOM_AUTH failure from the SRP errors above, which share Cognito codes.
        throw new HandoffError(HANDOFF_ERROR_CODES.completeFailed);
    }
};
